import { lazy, Suspense, useEffect, useState } from "react";
import { Toaster } from "react-hot-toast";
import { IntlProvider } from "react-intl";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import { Spinner } from "@/components/Spinner";
import { Layout } from "@/Layout";
import { JoiningPage } from "@/pages/JoiningPage";
import { JoinPage } from "@/pages/JoinPage";
import { TeamStatusPage } from "@/pages/TeamStatusPage";
import availableLanguages, { type MessageLoader } from "@/translations/index";

const AdminPage = lazy(() => import("@/pages/AdminPage"));
const ScoreOverviewPage = lazy(() => import("@/pages/ScoreOverviewPage"));
const TeamDetailPage = lazy(() => import("@/pages/TeamDetailPage"));
const ChallengeDetailPage = lazy(() => import("@/pages/ChallengeDetailPage"));
const CtfPage = lazy(() => import("@/pages/CtfPage"));

interface SimplifiedTeamStatusResponse {
  name: string;
  // set for the admin account and for LTI instructors, who also have their own team
  isAdmin?: boolean;
}

async function fetchTeamStatusData(): Promise<SimplifiedTeamStatusResponse | null> {
  const response = await fetch(`/multi-juicer/api/teams/me/status`);
  if (!response.ok) {
    return null;
  }
  const status = (await response.json()) as SimplifiedTeamStatusResponse;
  return status;
}

const LOCALE_STORAGE_KEY = "multijuicer:locale";

function App() {
  const [locale, setLocale] = useState(() => {
    // Try to get locale from localStorage first
    const storedLocale = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (storedLocale) {
      return storedLocale;
    }
    // Fall back to navigator language
    const navigatorLocale = navigator.language;
    if (navigatorLocale.startsWith("en")) {
      return "en";
    }
    return navigatorLocale;
  });
  const [messages, setMessages] = useState({});
  const [activeTeam, setActiveTeam] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Load initial messages for the stored locale
  useEffect(() => {
    async function loadInitialMessages() {
      const storedLocale = localStorage.getItem(LOCALE_STORAGE_KEY);
      if (storedLocale && storedLocale !== "en") {
        const language = availableLanguages.find(
          (lang) => lang.key === storedLocale
        );
        if (language) {
          const { default: messages } = await language.messageLoader();
          setMessages(messages);
        }
      }
    }
    loadInitialMessages();
  }, []);

  useEffect(() => {
    async function updateStatusData() {
      const status = await fetchTeamStatusData();
      if (!status) {
        return;
      }
      setActiveTeam(status.name);
      setIsAdmin(status.isAdmin === true || status.name === "admin");
    }
    updateStatusData();
  }, []);

  const switchLanguage = async ({
    key,
    messageLoader,
  }: {
    key: string;
    messageLoader: MessageLoader;
  }) => {
    const { default: messages } = await messageLoader();

    setMessages(messages);
    setLocale(key);
    localStorage.setItem(LOCALE_STORAGE_KEY, key);
  };

  return (
    <IntlProvider defaultLocale="en" locale={locale} messages={messages}>
      <BrowserRouter basename="/multi-juicer">
        <Routes>
          <Route
            path="/ctf"
            element={
              <Suspense fallback={<Spinner />}>
                <CtfPage />
              </Suspense>
            }
          />
          <Route
            path="/*"
            element={
              <Layout
                activeTeam={activeTeam}
                isAdmin={isAdmin}
                switchLanguage={switchLanguage}
                selectedLocale={locale}
                setActiveTeam={setActiveTeam}
              >
                <Suspense fallback={<Spinner />}>
                  <Routes>
                    <Route
                      path="/"
                      element={
                        <JoinPage
                          activeTeam={activeTeam}
                          setActiveTeam={setActiveTeam}
                        />
                      }
                    />
                    <Route path="/admin" element={<AdminPage />} />
                    <Route
                      path="/teams/:team/status/"
                      element={<TeamStatusPage setActiveTeam={setActiveTeam} />}
                    />
                    <Route
                      path="/teams/:team/joining/"
                      element={<JoiningPage setActiveTeam={setActiveTeam} />}
                    />
                    <Route
                      path="/score-overview"
                      element={
                        activeTeam ? (
                          <ScoreOverviewPage activeTeam={activeTeam} />
                        ) : (
                          <JoinPage
                            activeTeam={activeTeam}
                            setActiveTeam={setActiveTeam}
                          />
                        )
                      }
                    />
                    <Route
                      path="/score-overview/teams/:team"
                      element={
                        activeTeam ? (
                          <TeamDetailPage />
                        ) : (
                          <JoinPage
                            activeTeam={activeTeam}
                            setActiveTeam={setActiveTeam}
                          />
                        )
                      }
                    />
                    <Route
                      path="/score-overview/challenges/:challengeKey"
                      element={
                        activeTeam ? (
                          <ChallengeDetailPage />
                        ) : (
                          <JoinPage
                            activeTeam={activeTeam}
                            setActiveTeam={setActiveTeam}
                          />
                        )
                      }
                    />
                  </Routes>
                </Suspense>
              </Layout>
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </IntlProvider>
  );
}

export default App;
